/**
 * Canonicalls continuity carry — local lineage memory across traversal.
 * Not authentication; continuity persistence only.
 */
import type { PreservedCrossPost } from "./verifiedCrossPost";
import {
  preferAheadTraversalStep,
  resolveCanonicallsTraversalStepWithPhase,
  type CanonicallsTraversalId,
} from "./canonicallsTraversal";
import { loadLocalCrossPost } from "./verifiedCrossPost";
import type { ContinuityEvidenceMeta } from "./canonicallsEvidence";
import { emptyCanonicalizeDemoCarry, isCanonicallsFreshCanonicalize } from "./canonicallsFreshDemo";

export const CONTINUITY_CARRY_STORAGE_KEY = "canonicalls-continuity-carry:v1" as const;

export type RepresentationDriftState =
  | "canonical"
  | "representation"
  | "derived-mirror"
  | "transformed"
  | "summarized"
  | "exported"
  | "archived"
  | "agreement-linked";

export type VerificationContinuityState = "none" | "local-snapshot" | "signed" | "inspectable";

export type LineageContinuityState = "dormant" | "active" | "replaying" | "inspecting";

export type CarriedRepresentation = {
  surface: string;
  drift: RepresentationDriftState;
};

export type CanonicallsContinuityCarry = {
  version: 1;
  postId: string | null;
  sourceTitle: string | null;
  actor: string | null;
  digest: string | null;
  lineageState: LineageContinuityState;
  verificationState: VerificationContinuityState;
  representationCount: number;
  representations: CarriedRepresentation[];
  agreementLinked: boolean;
  lastTraversalStep: CanonicallsTraversalId | null;
  updatedAt: string;
  evidence: ContinuityEvidenceMeta[];
};

export function emptyContinuityCarry(): CanonicallsContinuityCarry {
  return {
    version: 1,
    postId: null,
    sourceTitle: null,
    actor: null,
    digest: null,
    lineageState: "dormant",
    verificationState: "none",
    representationCount: 0,
    representations: [],
    agreementLinked: false,
    lastTraversalStep: null,
    updatedAt: new Date().toISOString(),
    evidence: [],
  };
}


export function coalesceContinuityCarry(
  carry: CanonicallsContinuityCarry | null | undefined,
): CanonicallsContinuityCarry {
  if (!carry || typeof carry !== "object") return emptyContinuityCarry();
  return {
    ...emptyContinuityCarry(),
    ...carry,
    version: 1,
    representations: Array.isArray(carry.representations) ? carry.representations : [],
    evidence: Array.isArray(carry.evidence) ? carry.evidence : [],
  };
}


export function isVerifyToReplayHandoff(carry: CanonicallsContinuityCarry): boolean {
  const c = coalesceContinuityCarry(carry);
  return (
    c.lineageState === "replaying" &&
    (c.verificationState === "inspectable" || c.verificationState === "signed")
  );
}

export function isInspectHandoffActive(carry: CanonicallsContinuityCarry): boolean {
  const c = coalesceContinuityCarry(carry);
  return c.lineageState === "inspecting" && c.verificationState !== "none";
}

export function formatDigestFragment(digest: string | null | undefined): string | null {
  const d = (digest ?? "").replace(/^sha256:/i, "").trim().toLowerCase();
  if (!d) return null;
  if (d.length >= 16) return `${d.slice(0, 8)}…${d.slice(-6)}`;
  return d.length > 4 ? `${d.slice(0, 4)}…` : d;
}

export function verificationLabel(state: VerificationContinuityState): string {
  if (state === "signed") return "Continuity confirmed";
  if (state === "local-snapshot") return "Continuity preserved";
  if (state === "inspectable") return "Continuity inspectable";
  return "";
}

export function lineageLabel(state: LineageContinuityState): string {
  if (state === "replaying") return "Memory";
  if (state === "inspecting") return "Inspecting";
  if (state === "active") return "Carried";
  return "Awaiting source";
}

function representationsFromPreserved(record: PreservedCrossPost): CarriedRepresentation[] {
  const base: CarriedRepresentation[] = [
    { surface: "Canonical record", drift: "canonical" },
    { surface: "Social post", drift: "representation" },
    { surface: "Professional network", drift: "representation" },
    { surface: "Community mirror", drift: "derived-mirror" },
  ];
  if (record.draft.aiAssisted) {
    base.push({ surface: "AI summary", drift: "summarized" });
  }
  if (record.mode === "signed") {
    base.push({ surface: "Signed export", drift: "exported" });
  }
  return base;
}

export function verificationStateFromRecord(record: PreservedCrossPost): VerificationContinuityState {
  if (record.verifyUrl && record.mode === "signed") return "signed";
  if (record.verifyUrl) return "inspectable";
  if (record.mode === "local") return "local-snapshot";
  return "none";
}

export function loadContinuityCarry(): CanonicallsContinuityCarry {
  if (typeof localStorage === "undefined") return emptyContinuityCarry();
  try {
    const raw = localStorage.getItem(CONTINUITY_CARRY_STORAGE_KEY);
    if (!raw) return emptyContinuityCarry();
    const parsed = JSON.parse(raw) as CanonicallsContinuityCarry;
    if (parsed?.version !== 1) return emptyContinuityCarry();
    return { ...emptyContinuityCarry(), ...parsed, version: 1 };
  } catch {
    return emptyContinuityCarry();
  }
}

export function saveContinuityCarry(patch: Partial<CanonicallsContinuityCarry>): CanonicallsContinuityCarry {
  const prev = coalesceContinuityCarry(loadContinuityCarry());
  const next: CanonicallsContinuityCarry = {
    ...prev,
    ...patch,
    version: 1,
    representations: patch.representations ?? prev.representations,
    evidence: patch.evidence ?? prev.evidence,
    updatedAt: new Date().toISOString(),
  };
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(CONTINUITY_CARRY_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota */
    }
  }
  return next;
}

export type MergeCarryFromPreservedOptions = {
  evidence?: ContinuityEvidenceMeta[];
  lastTraversalStep?: CanonicallsTraversalId;
  lineageState?: LineageContinuityState;
};

export function mergeCarryFromPreserved(
  record: PreservedCrossPost,
  options?: MergeCarryFromPreservedOptions,
): CanonicallsContinuityCarry {
  const prev = coalesceContinuityCarry(loadContinuityCarry());
  const reps = representationsFromPreserved(record);
  return saveContinuityCarry({
    postId: record.postId,
    sourceTitle: record.draft.title.trim() || "Canonical record",
    actor: record.draft.author.trim() || null,
    digest: record.digest || null,
    verificationState: verificationStateFromRecord(record),
    representationCount: Math.max(0, reps.length - 1),
    representations: reps,
    agreementLinked: false,
    lastTraversalStep: options?.lastTraversalStep ?? prev.lastTraversalStep ?? "origin",
    lineageState: options?.lineageState ?? "active",
    evidence: options?.evidence ?? prev.evidence,
  });
}

function lineageStateForPath(pathname: string, hash: string): LineageContinuityState {
  const p = pathname.replace(/\/$/, "") || "/";
  const h = hash.replace(/^#/, "").toLowerCase();
  if (p === "/replay") return "replaying";
  if (p === "/inspect" || h === "lineage") return "inspecting";
  if (p === "/canonicalize" || p === "/aether") return "active";
  return "dormant";
}

function agreementLinkedForPath(pathname: string, hash: string): boolean | undefined {
  const p = pathname.replace(/\/$/, "") || "/";
  const h = hash.replace(/^#/, "").toLowerCase();
  if (p === "/aether" && h === "agreement") return true;
  return undefined;
}

/** Hydrate carry from route + optional deep-linked post ids. */
export function syncCarryFromRoute(pathname: string, search = "", hash = ""): CanonicallsContinuityCarry {
  if (isCanonicallsFreshCanonicalize(pathname, search)) {
    return emptyCanonicalizeDemoCarry();
  }
  const params = new URLSearchParams(search);
  const postId =
    params.get("post")?.trim() ||
    params.get("cross_post")?.trim() ||
    loadContinuityCarry().postId ||
    null;

  const step = resolveCanonicallsTraversalStepWithPhase(pathname, search, hash);
  const digestParam = params.get("sha")?.trim().toLowerCase().replace(/^sha256:/, "");
  const digestFromUrl = digestParam && /^[a-f0-9]{64}$/.test(digestParam) ? digestParam : null;

  let carry = coalesceContinuityCarry(loadContinuityCarry());

  if (postId) {
    const record = loadLocalCrossPost(postId);
    if (record) {
      carry = mergeCarryFromPreserved(record, {
        lastTraversalStep: preferAheadTraversalStep(step?.id, carry.lastTraversalStep),
      });
    } else if (carry.postId !== postId) {
      carry = saveContinuityCarry({ postId, lineageState: "active" });
    }
  }

  const agreementPatch = agreementLinkedForPath(pathname, hash);
  const lineageState = lineageStateForPath(pathname, hash);
  const routePath = pathname.replace(/\/$/, "") || "/";

  const verificationState =
    digestFromUrl
      ? "inspectable"
      : routePath === "/replay" && (carry.verificationState === "inspectable" || carry.verificationState === "signed")
        ? carry.verificationState
        : routePath === "/inspect" && carry.verificationState === "none" && carry.digest
          ? "inspectable"
          : carry.verificationState;

  return saveContinuityCarry({
    lastTraversalStep:
      routePath === "/canonicalize"
        ? preferAheadTraversalStep(step?.id, carry.lastTraversalStep)
        : (step?.id ?? carry.lastTraversalStep),
    lineageState,
    agreementLinked: agreementPatch ?? carry.agreementLinked,
    digest: digestFromUrl ?? carry.digest,
    verificationState,
  });
}

export function clearContinuityCarry(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(CONTINUITY_CARRY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
